
import React from 'react';
import { IntegrationApp, Language } from '../../types';

interface IntegrationsLiveProps {
  language: Language;
  selectedApp: IntegrationApp;
  userLocation: {lat: number, lng: number} | null;
  activeDestination: string;
  isNavigating: boolean;
  isAiSpeaking: boolean;
  aiMapResponse: string;
  navStepIndex: number;
  mockRouteEvents: string[];
  isSimulating: boolean;
  setIsSimulating: (val: boolean) => void;
  callStatus: 'incoming' | 'active' | 'ended';
  setCallStatus: (status: 'incoming' | 'active' | 'ended') => void;
  callTranscript: {original: string, translated: string, isUser: boolean}[];
  setActiveTab: (tab: 'dashboard' | 'settings' | 'live') => void;
}

const IntegrationsLive: React.FC<IntegrationsLiveProps> = ({
  language, selectedApp, userLocation, activeDestination, isNavigating, isAiSpeaking,
  aiMapResponse, navStepIndex, mockRouteEvents, isSimulating, setIsSimulating,
  callStatus, setCallStatus, callTranscript, setActiveTab
}) => {
  const isRtl = language === 'ar';

  if (selectedApp.id === 'google-maps') {
    const lat = userLocation?.lat || 24.7136;
    const lng = userLocation?.lng || 46.6753;
    const mapUrl = activeDestination 
       ? `https://www.google.com/maps?q=${encodeURIComponent(activeDestination)}&output=embed`
       : `https://maps.google.com/maps?q=${lat},${lng}&t=m&z=15&output=embed&iwloc=near`;

    return (
       <div className="relative w-full h-[600px] rounded-3xl overflow-hidden bg-slate-900 border border-slate-800 shadow-2xl flex flex-col animate-in zoom-in-95 duration-500">
          <iframe width="100%" height="100%" frameBorder="0" src={mapUrl} className="flex-1 filter grayscale-[20%] contrast-[110%] pointer-events-auto" allowFullScreen></iframe>
          <div className="absolute top-0 left-0 right-0 p-6 z-20 flex justify-between items-start pointer-events-none">
             <div className="flex items-center gap-4 bg-white/90 dark:bg-black/80 p-3 rounded-2xl backdrop-blur-md shadow-lg pointer-events-auto min-w-[200px]">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white text-2xl shadow-lg transition-colors ${isNavigating ? 'bg-blue-500' : 'bg-[#0F9D58]'}`}><i className={`fas ${isNavigating ? 'fa-location-arrow' : 'fa-map-pin'}`}></i></div>
                <div>
                   <h2 className="text-lg font-black text-slate-800 dark:text-white leading-tight">{isNavigating ? 'Navigating' : 'Location'}</h2>
                   <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 truncate max-w-[150px]">{activeDestination || 'Current Position'}</p>
                </div>
             </div>
          </div>
          {isNavigating && (
             <div className="absolute top-24 left-6 z-20 pointer-events-none animate-in slide-in-from-left-4">
                <div className="bg-emerald-600 text-white p-4 rounded-xl shadow-2xl max-w-[250px] border-l-4 border-white/50">
                    <div className="flex items-center gap-3 mb-2"><i className="fas fa-arrow-up text-2xl"></i><span className="text-2xl font-black">200m</span></div>
                    <p className="text-xs font-medium opacity-90">{mockRouteEvents[navStepIndex] || 'Follow the route'}</p>
                </div>
             </div>
          )}
          {aiMapResponse && (
             <div className="absolute bottom-24 left-6 right-6 z-20 flex justify-center pointer-events-none">
                <div className="bg-black/80 backdrop-blur-xl px-6 py-4 rounded-2xl border border-white/10 shadow-2xl max-w-lg text-center animate-in slide-in-from-bottom-2">
                   <p className="text-white font-black text-sm lg:text-base leading-relaxed" dir="rtl">{aiMapResponse}</p>
                </div>
             </div>
          )}
          <div className="absolute bottom-0 left-0 right-0 bg-white/90 dark:bg-[#1a1a1a]/90 backdrop-blur-md border-t border-slate-200 dark:border-white/10 p-4 z-20 flex justify-between items-center">
             <div>
                <p className="text-slate-800 dark:text-white font-black text-lg">{new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                <p className="text-emerald-500 text-[10px] font-bold uppercase tracking-wider">{userLocation ? `${userLocation.lat.toFixed(4)}, ${userLocation.lng.toFixed(4)}` : 'Waiting for GPS...'}</p>
             </div>
             <button onClick={() => setIsSimulating(!isSimulating)} className={`px-6 py-3 rounded-xl font-black text-xs shadow-lg transition-all ${isSimulating ? 'bg-rose-500 text-white hover:bg-rose-600' : 'bg-emerald-500 text-white hover:bg-emerald-600'}`}>
                {isSimulating ? 'Stop' : 'Resume'}
             </button>
          </div>
       </div>
    );
 }
 
 if (selectedApp.id === 'phone-translator') {
    return (
       <div className="flex justify-center h-[600px] items-center bg-black/5 rounded-3xl">
          <div className="relative w-[340px] h-[600px] bg-black rounded-[45px] border-[8px] border-slate-800 shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-500">
             {callStatus === 'incoming' ? (
               <div className="flex-1 flex flex-col items-center pt-20 px-8 bg-gradient-to-b from-slate-900 via-slate-800 to-black relative">
                  <button onClick={() => setCallStatus('active')} className="w-16 h-16 rounded-full bg-emerald-500 text-white flex items-center justify-center text-2xl shadow-lg active:scale-95 transition-transform animate-bounce"><i className="fas fa-phone"></i></button>
               </div>
             ) : callStatus === 'active' ? (
                <div className="flex-1 flex flex-col bg-slate-900 relative">
                    <div className="flex-1 p-4 overflow-y-auto space-y-4 custom-scrollbar bg-black/50">
                       {callTranscript.map((line, idx) => (
                          <div key={idx} className="bg-slate-800/80 p-3 rounded-2xl rounded-tl-none animate-in fade-in slide-in-from-left-2">
                             <p className="text-xs text-white mb-2">{line.original}</p>
                             <p className="text-sm font-bold text-emerald-100" dir="rtl">{line.translated}</p>
                          </div>
                       ))}
                    </div>
                </div>
             ) : (
                <div className="flex-1 flex flex-col items-center justify-center bg-slate-900 text-center">
                   <h3 className="text-white text-xl font-bold mb-2">Call Ended</h3>
                   <button onClick={() => { setIsSimulating(false); setActiveTab('dashboard'); }} className="text-emerald-500 font-bold text-sm hover:underline">Close</button>
                </div>
             )}
          </div>
       </div>
    );
 }

 return <div className="p-10 text-center text-slate-400">Simulation not available for this app.</div>;
};

export default IntegrationsLive;
